import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cachedPatientService } from '../services/cachedApiService';
import { Patient, MedicalProblem, PriorAuthorization } from '../types';
import Layout from '../components/Layout';
import '../styles/RaisePAForm.css';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';
import api, { initCSRF, getCSRFTokenFromCookie } from "../services/api";

// On app start
const RaisePAForm: React.FC = () => {
  useEffect(() => {
    // Initialize CSRF token on component mount
    initCSRF();
  }, []);
  const { patientId } = useParams<{ patientId: string }>();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [problems, setProblems] = useState<MedicalProblem[]>([]);

  type Practitioner = { id: number; fname: string; lname: string; specialty?: string };
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const { user } = useAuth();
  const { invalidate } = useCache();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [letterUrl, setLetterUrl] = useState<string | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [isEpaDone, setIsEpaDone] = useState<boolean>(false);
  const [mcpPayload, setMcpPayload] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // We'll use a simpler approach without progress indicators
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Form state
  const [requestType, setRequestType] = useState<'prescription' | 'procedure'>('prescription');
  const [referenceId, setReferenceId] = useState<string>('');
  const [diagnosisId, setDiagnosisId] = useState<string>('');
  const [urgency, setUrgency] = useState<'standard' | 'urgent'>('standard');
  const [clinicalReason, setClinicalReason] = useState<string>('');
  const [letterText, setLetterText] = useState<string | null>(null);
  const letterTextRef = useRef<string | null>(null);

  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState<boolean>(false);

  // Approval state toggles showing final generation button
  const [isLetterApproved, setIsLetterApproved] = useState<boolean>(false);

  // Fetch patient & practitioners data
  useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId) return;
      setLoading(true);
      try {
        const data = await cachedPatientService.getPatientDetail(patientId);
        setPatient(data.patient);
        setProblems(data.problems);

        const response = await fetch('https://innovation.neutrino-ai.com/api/practitioners/');
        const practitionersData = await response.json();
        setPractitioners(practitionersData);
        
        // Find the provider based on the logged-in user's email
        const loggedInProvider = practitionersData.find(
          (provider: any) => provider.email === user?.username
        );
        
        if (loggedInProvider) {
          setProviderId(loggedInProvider.id.toString());
        } else if (practitionersData.length > 0) {
          // If no matching provider found but we have providers, select the first one
          setProviderId(practitionersData[0].id.toString());
        }
      } catch (err) {
        setError('Failed to fetch patient or practitioner data');
      }
      setLoading(false);
    };
    fetchPatientData();
  }, [patientId, user]);

  // Preview letter text request to backend using Web Worker
  const handlePreviewLetter = async () => {
    if (!patientId) return;
    setError(null);
    setSuccessMessage(null);
    setLetterUrl(null);
    setIsEpaDone(false);
    setMcpPayload(null);

    const priorAuthData: Partial<PriorAuthorization> = {
      patient: patientId,
      request_type: requestType,
      reference_id: referenceId ? parseInt(referenceId) : null,
      diagnosis: diagnosisId ? parseInt(diagnosisId) : null,
      provider: providerId ? parseInt(providerId) : null,
      urgency,
      clinical_reason: clinicalReason || null,
    };

    try {
      setIsPreviewLoading(true);
      setIsGenerating(true);
      
      // Get CSRF token from cookie
      const csrfToken = getCSRFTokenFromCookie() || '';
      
      // Create a worker to handle the API call
   const workerCode = `
  self.onmessage = async function(e) {
    try {
      // Log the CSRF token being used
      console.log('Using CSRF token in preview worker:', e.data.csrfToken);
      
      const response = await fetch(e.data.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': e.data.csrfToken // This will be the token from cookie
        },
        body: JSON.stringify(e.data.priorAuthData),
        credentials: 'include'   // <-- ensure cookies also go with request
      });
      
      const data = await response.json();
      self.postMessage({
        status: 'success',
        data: data,
        ok: response.ok
      });
    } catch (error) {
      self.postMessage({
        status: 'error',
        error: error.message
      });
    }
  }
`;

      
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const worker = new Worker(URL.createObjectURL(blob));
      
      // Set up message handler
      worker.onmessage = (e) => {
        const result = e.data;
        
        if (result.status === 'error') {
          setError('Error generating preview letter: ' + result.error);
          setIsPreviewLoading(false);
          worker.terminate();
          return;
        }
        
        const { data, ok } = result;
        
        if (ok) {
          // Check if ePA is done
          if (data.epa_done && data.mcp_payload) {
            // If ePA is done, show the payload
            setIsEpaDone(true);
            setMcpPayload(data.mcp_payload);
            setShowPreview(true);
            setIsLetterApproved(false);
          } else if (data.letter_text) {
            // If ePA is not done, show the letter preview
            letterTextRef.current = data.letter_text;
            setLetterText(data.letter_text);
            setShowPreview(true);
            setIsLetterApproved(false); // reset approval on new preview
          } else {
            setError('No letter text or payload received from server.');
          }
        } else {
          setError(data?.error || 'Could not generate letter preview.');
        }
        
        setIsPreviewLoading(false);
        setIsGenerating(false);
        worker.terminate(); // Clean up the worker
      };
      
      // Handle worker errors
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setError('Error in worker: ' + error.message);
        setIsPreviewLoading(false);
        setIsGenerating(false);
        worker.terminate();
      };
      
      // Start the worker
      worker.postMessage({
        url: `https://innovation.neutrino-ai.com/api/raise-pa-preview/${patientId}/`,
        priorAuthData,
        csrfToken
      });
      
    } catch (err) {
      setError('Error setting up worker for preview letter generation.');
      setIsPreviewLoading(false);
      setIsGenerating(false);
    }
  };

  // User approves the letter preview
  const handleApproveLetter = () => {
    setIsLetterApproved(true);
    setShowPreview(false);
    setSuccessMessage(null);
    setError(null);
  };

  // Final API call to generate and store letter PDF using Web Worker
  const handleSubmitLetter = async () => {
    if (!patientId) {
      setError('Patient ID is required');
      return;
    }
    setError(null);
    setSuccessMessage(null);
    setIsSubmittingFinal(true);
    setIsGenerating(true);
    
    try {
      const priorAuthData: Partial<PriorAuthorization> = {
        patient: patientId,
        request_type: requestType,
        reference_id: referenceId ? parseInt(referenceId) : null,
        diagnosis: diagnosisId ? parseInt(diagnosisId) : null,
        provider: providerId ? parseInt(providerId) : null,
        urgency,
        clinical_reason: clinicalReason || null,
      };

      // Get CSRF token from cookie
      const csrfToken = getCSRFTokenFromCookie() || '';

      // Create a worker to handle the API call
      const workerCode = `
        self.onmessage = async function(e) {
          try {
            // Log the CSRF token being used
            console.log('Using CSRF token in final submission worker:', e.data.csrfToken);
            
            const response = await fetch(e.data.url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': e.data.csrfToken // This will be the token from cookie
              },
              credentials: 'include',   // <-- ensure cookies also go with request
              body: JSON.stringify(e.data.priorAuthData)
            });
            
            const data = await response.json();
            self.postMessage({
              status: 'success',
              data: data,
              ok: response.ok
            });
          } catch (error) {
            self.postMessage({
              status: 'error',
              error: error.message
            });
          }
        }
      `;
      
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const worker = new Worker(URL.createObjectURL(blob));
      
      // Set up message handler
      worker.onmessage = (e) => {
        const result = e.data;
        
        if (result.status === 'error') {
          setError('Error submitting final PA letter: ' + result.error);
          setIsSubmittingFinal(false);
          worker.terminate();
          return;
        }
        
        const { data, ok } = result;
        
        if (ok) {
          if (data.epa_done) {
            // If ePA is done, show success message with payload
            setMcpPayload(data.mcp_payload);
            setSuccessMessage('Prior Authorization has been successfully submitted with ePA!');
            setIsLetterApproved(false);
            setLetterText(null);
            letterTextRef.current = null;
          } else if (data?.letter_file) {
            // If ePA is not done, show success message with letter file
            setLetterUrl(data.letter_file);
            setSuccessMessage('Prior Authorization letter has been successfully generated and stored!');
            setIsLetterApproved(false);
            setLetterText(null);
            letterTextRef.current = null;
          } else {
            setError('No letter file or payload received from server.');
          }
        } else {
          setError(data?.error || data?.message || 'Failed to generate final letter.');
        }
        
        setIsSubmittingFinal(false);
        setIsGenerating(false);
        worker.terminate(); // Clean up the worker
      };
      
      // Handle worker errors
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setError('Error in worker: ' + error.message);
        setIsSubmittingFinal(false);
        setIsGenerating(false);
        worker.terminate();
      };
      
      // Start the worker
      worker.postMessage({
        url: `https://innovation.neutrino-ai.com/api/raise-pa/${patientId}/`,
        priorAuthData,
        csrfToken
      });
      
    } catch (err) {
      setError('Error setting up worker for final PA letter generation.');
      setIsSubmittingFinal(false);
      setIsGenerating(false);
    }
  };

  // Close preview on Escape key press
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPreview) setShowPreview(false);
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [showPreview]);

  // Reset approval state if user edits any form input after approval
  function resetApprovalIfNeeded() {
    if (isLetterApproved) {
      setIsLetterApproved(false);
      setSuccessMessage(null);
      setLetterUrl(null);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading patient data...</div>
        </div>
      </Layout>
    );
  }

  if (error && !patient) {
    return (
      <Layout>
        <div className="error" style={{ marginTop: 20 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '10px' }}>
            <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>{error}</div>
          <button
            onClick={() => {
              invalidate.patients(patientId);
              window.location.reload();
            }}
            className="btn btn-primary mt-3"
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pa-form-container">
        <h2>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: '10px', verticalAlign: 'middle' }}>
            <path d="M14 3V7C14 7.55228 14.4477 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Raise Prior Authorization
        </h2>

        <div className="patient-info">
          <p>
            <strong>Patient:</strong> {patient?.fname} {patient?.lname} ({patient?.pid})
          </p>
        </div>

        <form onSubmit={e => { e.preventDefault(); handlePreviewLetter(); }}>
          {successMessage && (
            <div className="success-message" style={{ color: 'green', marginBottom: '15px', padding: '10px', backgroundColor: '#f0fff0', borderRadius: '5px', borderLeft: '4px solid green' }}>
              <strong>Success:</strong> {successMessage}
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="requestType" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#2d3748'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V15" stroke="#4a5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 15L21 3" stroke="#4a5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 3H21V9" stroke="#4a5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Request Type
              </span>
            </label>
            <select
              id="requestType"
              value={requestType}
              onChange={e => {
                setRequestType(e.target.value as 'prescription' | 'procedure');
                resetApprovalIfNeeded();
              }}
              required
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                fontSize: '14px',
                color: '#2d3748'
              }}
            >
              <option value="prescription">Prescription</option>
              <option value="procedure">Procedure</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="referenceId" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#2d3748'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 13C10 14.1046 10.8954 15 12 15C13.1046 15 14 14.1046 14 13C14 11.8954 13.1046 11 12 11C10.8954 11 10 11.8954 10 13Z" stroke="#4a5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.4697 9.53033C13.7626 9.23744 14.2374 9.23744 14.5303 9.53033L17.5 12.5L14.5303 15.4697C14.2374 15.7626 13.7626 15.7626 13.4697 15.4697C13.1768 15.1768 13.1768 14.7019 13.4697 14.409L15.3787 12.5L13.4697 10.591C13.1768 10.2981 13.1768 9.82322 13.4697 9.53033Z" fill="#4a5568"/>
                  <path d="M10.5303 9.53033C10.8232 9.23744 10.8232 8.76256 10.5303 8.46967C10.2374 8.17678 9.76256 8.17678 9.46967 8.46967L6.5 11.4393L9.46967 14.409C9.76256 14.7019 10.2374 14.7019 10.5303 14.409C10.8232 14.1161 10.8232 13.6412 10.5303 13.3483L8.62132 11.4393L10.5303 9.53033Z" fill="#4a5568"/>
                  <path d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z" stroke="#4a5568" strokeWidth="2"/>
                </svg>
                Reference ID
              </span>
            </label>
            <input
              type="number"
              id="referenceId"
              value={referenceId}
              onChange={e => {
                setReferenceId(e.target.value);
                resetApprovalIfNeeded();
              }}
              placeholder="Enter reference ID"
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                fontSize: '14px',
                color: '#2d3748'
              }}
            />
            <small style={{ color: '#718096', marginTop: '4px', display: 'block' }}>ID of the prescription or procedure requiring authorization</small>
          </div>

  

          <div className="form-group">
            <label htmlFor="diagnosisId">Diagnosis</label>
            <select
              id="diagnosisId"
              value={diagnosisId}
              onChange={e => {
                setDiagnosisId(e.target.value);
                resetApprovalIfNeeded();
              }}
            >
              <option value="">Select a diagnosis</option>
              {problems.map(problem => (
                <option key={problem.id} value={problem.id}>
                  {problem.title} ({problem.diagnosis_code})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="urgency">Urgency</label>
            <select
              id="urgency"
              value={urgency}
              onChange={e => {
                setUrgency(e.target.value as 'standard' | 'urgent');
                resetApprovalIfNeeded();
              }}
              required
            >
              <option value="standard">Standard</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="clinicalReason">Clinical Reason</label>
            <textarea
              id="clinicalReason"
              value={clinicalReason}
              onChange={e => {
                setClinicalReason(e.target.value);
                resetApprovalIfNeeded();
              }}
              placeholder="Enter clinical reason for the authorization"
              rows={4}
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="providerId">
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" fill="#4a5568" />
                  <path d="M12 13C7.58172 13 4 16.5817 4 21H20C20 16.5817 16.4183 13 12 13Z" fill="#4a5568" />
                </svg>
                Provider
              </span>
            </label>
            <div
              style={{
                padding: '10px 14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#4299e1',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {(practitioners.find(p => p.id.toString() === providerId)?.fname || '').charAt(0)}
                {(practitioners.find(p => p.id.toString() === providerId)?.lname || '').charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: 'bold', color: '#2d3748' }}>
                  {practitioners.find(p => p.id.toString() === providerId)?.fname || ''} {practitioners.find(p => p.id.toString() === providerId)?.lname || ''}
                </div>
                {practitioners.find(p => p.id.toString() === providerId)?.specialty && (
                  <div style={{ fontSize: '12px', color: '#718096' }}>
                    {practitioners.find(p => p.id.toString() === providerId)?.specialty}
                  </div>
                )}
              </div>
            </div>
            <small style={{ color: '#718096', marginTop: '4px', display: 'block' }}>Provider requesting the authorization</small>
          </div>

          <div className="form-actions" style={{
            marginTop: '30px',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <button
              type="button"
              onClick={handlePreviewLetter}
              disabled={isPreviewLoading || isSubmittingFinal}
              style={{
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#4299e1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 3V7C14 7.55228 14.4477 8 15 8H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 15L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 11L11 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {isPreviewLoading ? 'Generating Preview...' : 'Preview Letter'}
              {isPreviewLoading && (
                <span className="loading-indicator">
                  <span className="loading-spinner"></span>
                </span>
              )}
            </button>

            {isLetterApproved && (
              <button
                type="button"
                onClick={handleSubmitLetter}
                disabled={isSubmittingFinal}
                style={{
                  backgroundColor: '#48bb78',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 13L9 17L19 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {isSubmittingFinal ? 'Generating Final Letter...' : 'Generate Final Letter'}
              </button>
            )}

            <Link
              to={`/patients/${patientId}`}
              style={{
                backgroundColor: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                textDecoration: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Cancel
            </Link>
          </div>
        </form>

        {(letterUrl || (isEpaDone && mcpPayload)) && (
          <div className="pa-letter-preview" style={{ marginTop: '20px' }}>
            {isEpaDone && mcpPayload ? (
              <>
                <h4>📄 ePA Submitted Successfully</h4>
                <div style={{
                  backgroundColor: '#f0f8ff',
                  padding: '15px',
                  borderRadius: '8px',
                  marginTop: '10px',
                  border: '1px solid #cce5ff'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>MCP Payload Used:</div>
                  <pre style={{
                    whiteSpace: 'pre-wrap',
                    overflowX: 'auto',
                    backgroundColor: 'white',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {JSON.stringify(mcpPayload, null, 2)}
                  </pre>
                </div>
              </>
            ) : (
              <h4>📄 PA Letter Generated</h4>
            )}
          </div>
        )}

                {/* Letter Preview Section */}
          {showPreview && (
            <div className="letter-preview-section" style={{
              margin: '20px 0',
              padding: '20px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '2px solid #4299e1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '15px',
                color: '#2b4c7e',
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: '10px'
              }}>
                {isEpaDone ? 'ePA Payload Preview' : 'Prior Authorization Letter Preview'}
              </h3>

              {isEpaDone && mcpPayload ? (
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '20px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  <div style={{ marginBottom: '15px', color: '#2b4c7e', fontWeight: 'bold' }}>
                    ePA is enabled for this patient. The following payload will be used:
                  </div>
                  <pre style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                    {JSON.stringify(mcpPayload, null, 2)}
                  </pre>
                </div>
              ) : letterTextRef.current && (
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '20px',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {letterTextRef.current}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleApproveLetter}
                  style={{
                    backgroundColor: '#48bb78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    flex: '1'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 13L9 17L19 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {isEpaDone ? 'Approve & Submit ePA' : 'Approve & Generate Final Letter'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  style={{
                    backgroundColor: '#e2e8f0',
                    color: '#4a5568',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Cancel
                </button>
              </div>
            </div>
          )}
{/* Simple Generation Indicator */}
{isGenerating && (
  <div className="generation-indicator">
    <div className="loading-spinner"></div>
    <p>Generating letter... This will continue even if you switch tabs or log out.</p>
  </div>
)}

{error && (
  <div className="error-message" style={{ color: 'red', marginTop: '10px', padding: '10px', backgroundColor: '#ffeeee', borderRadius: '5px' }}>
    <strong>Error:</strong> {error}
  </div>
)}

   
      </div>
    </Layout>
  );
};

export default RaisePAForm;
